<template>
  <div
    ref="containerRef"
    class="turnstile-widget"
  />
</template>

<script setup lang="ts">
  import { onBeforeUnmount, onMounted, ref } from 'vue'

  const emit = defineEmits<{
    (e: 'verified', token: string): void
    (e: 'expired'): void
    (e: 'error'): void
  }>()

  const config = useRuntimeConfig()
  const sitekey = (config.public.turnstileSiteKey as string) || ''

  const containerRef = ref<HTMLElement | null>(null)
  let widgetId: string | null = null

  const SCRIPT_SRC =
    'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

  function loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') return resolve()
      if ((window as any).turnstile) return resolve()
      const existing = document.querySelector<HTMLScriptElement>(
        `script[src^="${SCRIPT_SRC}"]`,
      )
      if (existing) {
        existing.addEventListener('load', () => resolve())
        existing.addEventListener('error', () =>
          reject(new Error('Turnstile script failed to load')),
        )
        return
      }
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.defer = true
      script.onload = () => resolve()
      script.onerror = () =>
        reject(new Error('Turnstile script failed to load'))
      document.head.appendChild(script)
    })
  }

  function renderWidget() {
    const turnstile = (window as any).turnstile
    if (!turnstile || !containerRef.value || !sitekey) return
    widgetId = turnstile.render(containerRef.value, {
      sitekey,
      callback: (token: string) => emit('verified', token),
      'expired-callback': () => emit('expired'),
      'error-callback': () => emit('error'),
    })
  }

  function reset() {
    const turnstile = (window as any).turnstile
    if (turnstile && widgetId) turnstile.reset(widgetId)
  }

  onMounted(async () => {
    if (!sitekey) {
      console.warn('TurnstileWidget: NUXT_PUBLIC_TURNSTILE_SITE_KEY is not set')
      return
    }
    try {
      await loadScript()
      renderWidget()
    } catch (e) {
      console.error(e)
      emit('error')
    }
  })

  onBeforeUnmount(() => {
    const turnstile = (window as any).turnstile
    if (turnstile && widgetId) {
      try {
        turnstile.remove(widgetId)
      } catch {
        /* ignore */
      }
    }
  })

  defineExpose({ reset })
</script>

<style scoped>
  .turnstile-widget {
    display: flex;
    justify-content: center;
    margin-top: var(--space-12);
  }
</style>
